/* eslint react-hooks/exhaustive-deps: 0 */ // --> OFF

import {
  Button,
  ChatSupport,
  FormCard,
  MessageTile,
  TextField,
} from "@suralink/samurai/ui";
import { Alert, Error } from "@suralink/samurai/icons";
import { SuraLogoWithText } from "@suralink/samurai/visuals";
import WarningAlert from "../icons/WarningAlert";
import { RouteComponentProps } from "react-router-dom";
import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { getQueryParamsFromUrl } from "../utils/helpers";
import styles from "../styles/Login.module.scss";
import * as actions from "../redux/actions";

interface ComponentProps {}

interface StateProps {
  passwordSaved: boolean;
  resetPasswordErrorState: ErrorStateModel;
}

interface DispatchProps {
  resetPasswordAction: (
    email: string,
    resetToken: string,
    password: string
  ) => any;
}

type Props = StateProps & DispatchProps & ComponentProps & RouteComponentProps;

const ResetPassword: React.FC<Props> = ({
  match,
  history,
  resetPasswordAction,
  passwordSaved,
  resetPasswordErrorState,
}) => {
  const { resetToken } = match.params as { [key: string]: string };
  const queryParams = getQueryParamsFromUrl(window.location.href);
  const { email, clientId, redirectUri, state } = queryParams;
  const [password, setPassword] = useState("");
  const [verifyPassword, setVerifyPassword] = useState("");
  const [showPassFormErr, setShowPassFormErr] = useState(false);
  const [showConfirmFormErr, setShowConfirmFormErr] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const lengthCheck = password.length >= 8;
  const caseCheck = !!password.match(/[A-Z]/) && !!password.match(/[a-z]/);
  const alphaNumCheck = !!password.match(/[0-9]/) && !!password.match(/[a-z]/i);
  const specialCharCheck = !!password.match(/\W|_/);
  const matchCheck =
    password === verifyPassword && password !== "" && verifyPassword !== "";
  const checkAll =
    lengthCheck && caseCheck && alphaNumCheck && specialCharCheck && matchCheck;

  // if critical url params are missing send them to suralink.com
  useEffect(() => {
    if (!email) {
      window.location.href = "https://www.suralink.com";
    }
    return;
  });

  useEffect(() => {
    if (passwordSaved) {
      history.push(
        `/login?clientId=${clientId}&redirectUri=${redirectUri}&state=${state}`
      );
    }
  }, [passwordSaved]);

  useEffect(() => {
    if (resetPasswordErrorState.status !== 0) {
      setShowPassFormErr(true);
      setShowConfirmFormErr(true);
      setErrorMessage(resetPasswordErrorState.message);
    }
  }, [resetPasswordErrorState]);

  const verifyAndSetPassword = () => {
    if (checkAll) {
      resetPasswordAction(email, password, resetToken);
    } else if (!password.length && verifyPassword.length) {
      setShowPassFormErr(true);
      setErrorMessage("Password field cannot be empty.");
    } else if (!verifyPassword.length && password.length) {
      setShowConfirmFormErr(true);
      setErrorMessage("Verify password field cannot be empty.");
    } else if (!verifyPassword.length && !password.length) {
      setShowPassFormErr(true);
      setShowConfirmFormErr(true);
      setErrorMessage("All fields are required.");
    } else if (!matchCheck) {
      setShowPassFormErr(true);
      setShowConfirmFormErr(true);
      setErrorMessage("Verified password does not match new password.");
    } else {
      setShowPassFormErr(true);
      setShowConfirmFormErr(true);
      setErrorMessage(
        "All fields are required and password criteria must be met."
      );
    }
  };

  return (
    <div className={styles.passMain}>
      <div className={styles.center}>
        <div
          className={styles.formLeft}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.keyCode === 13) {
              verifyAndSetPassword();
            }
          }}
        >
          <FormCard
            style={{
              width: "443px",
              minHeight: "502px",
              background: "#fff",
              border: "1px solid #E7E9EB",
              borderRadius: "3px",
              margin: 0,
              padding: 0,
            }}
          >
            <div className={styles.logo}>
              <SuraLogoWithText width={150} height={47} />
            </div>

            <hr />
            <div className={styles.info}>
              <h3>Create a new password</h3>
              <p>Set a new password for your Suralink account.</p>
            </div>

            {showPassFormErr || showConfirmFormErr ? (
              <MessageTile
                message={errorMessage}
                state="error"
                icon={<Error />}
                style={{
                  width: "377px",
                  padding: "8px 0 8px 8px",
                  margin: "auto",
                }}
                messageTextStyles={{
                  fontSize: "12px",
                  color: "#595959",
                }}
                showDismiss={false}
              />
            ) : null}

            <TextField
              placeholder="Enter new password"
              label="New password"
              required
              showRequiredState={showPassFormErr && !matchCheck}
              requiredText={
                !matchCheck && showPassFormErr && password !== ""
                  ? "Passwords don’t match"
                  : ""
              }
              onChange={(e: any) => {
                setPassword(e.target.value);
              }}
              onFocus={() => {
                setShowPassFormErr(false);
              }}
              style={{
                width: "377px",
                height: "48px",
                margin: "32px auto",
              }}
              type="password"
              name="password"
              iconType="lockIcon"
            />

            <TextField
              placeholder="Verify new password"
              label=" Verify new password"
              required
              showRequiredState={showConfirmFormErr && !matchCheck}
              requiredText={
                !matchCheck && showConfirmFormErr && verifyPassword !== ""
                  ? "Passwords don’t match"
                  : ""
              }
              onChange={(e: any) => {
                setVerifyPassword(e.target.value);
              }}
              onFocus={() => {
                setShowConfirmFormErr(false);
              }}
              style={{
                width: "377px",
                height: "48px",
                margin: `${!matchCheck ? "48px auto" : "36px auto"}`,
              }}
              type="password"
              name="password"
              iconType="lockIcon"
            />
            <hr />

            <Button
              label="Change password"
              type="submit"
              color="blue"
              disabled={!checkAll}
              style={{
                width: "377px",
                height: "40px",
                margin: "24px auto",
              }}
              onClick={() => {
                verifyAndSetPassword();
              }}
            />
          </FormCard>
        </div>
        <div className={styles.right}>
          <div className={styles.infoText}>
            {!checkAll ? (
              <p>Your new password must meet the following requirements:</p>
            ) : (
              <h3>LOOKS GOOD!</h3>
            )}
          </div>
          <div className={styles.verificationCriteria}>
            <div className={styles.criteria}>
              <span className={styles.criteriaIcon}>
                {lengthCheck ? <Alert /> : <WarningAlert />}
              </span>
              <span
                className={styles.criteriaText}
                style={{
                  fontWeight: lengthCheck ? 500 : 600,
                }}
              >
                At least 8 characters
              </span>
            </div>
            <div className={styles.criteria}>
              <span className={styles.criteriaIcon}>
                {caseCheck ? <Alert /> : <WarningAlert />}
              </span>
              <span
                className={styles.criteriaText}
                style={{
                  fontWeight: caseCheck ? 500 : 600,
                }}
              >
                Both upper and lower case letters
              </span>
            </div>
            <div className={styles.criteria}>
              <span className={styles.criteriaIcon}>
                {alphaNumCheck ? <Alert /> : <WarningAlert />}
              </span>
              <span
                className={styles.criteriaText}
                style={{
                  fontWeight: alphaNumCheck ? 500 : 600,
                }}
              >
                A mixture of letters and numbers
              </span>
            </div>
            <div className={styles.criteria}>
              <span className={styles.criteriaIcon}>
                {specialCharCheck ? <Alert /> : <WarningAlert />}
              </span>
              <span
                className={styles.criteriaText}
                style={{
                  fontWeight: specialCharCheck ? 500 : 600,
                }}
              >
                At least one special character{" "}
              </span>
            </div>
            <div className={styles.criteria}>
              <span className={styles.criteriaIcon}>
                {matchCheck ? <Alert /> : <WarningAlert />}
              </span>
              <span
                className={styles.criteriaText}
                style={{
                  fontWeight: matchCheck ? 500 : 600,
                }}
              >
                Verify & new password must match
              </span>
            </div>
          </div>
          <ChatSupport style={{ marginTop: "47px" }} />
        </div>
      </div>
    </div>
  );
};

const mstp = (state: InitialStateModel): StateProps => {
  let { passwordSaved, resetPasswordErrorState } = state;
  return {
    passwordSaved,
    resetPasswordErrorState,
  };
};

const matp = ((): DispatchProps => {
  let { resetPasswordAction } = actions;
  return {
    resetPasswordAction,
  };
})();

export default connect(mstp, matp)(ResetPassword);
